import { BaseReplayController } from '@bn-games/history-core';
import {
  applyDraw,
  applyMove,
  applyPass,
  claimMuggins,
  createInitialState,
  expireMuggins,
  startNextHand,
  type GameStateData,
} from '../game/GameState.js';
import type { Turn } from '../game/types.js';
import { applyRuleOverrides, getRuleSet, type RuleOverrides } from '../game/variants/index.js';
import type { DominoRuleSet } from '../game/variants/types.js';
import type { GameRecord, RecordedMove, RecordedSeed } from './types.js';

/**
 * Rule overrides a stored seed carries. Records made before Козёл lack the
 * two toggles; they read as off, which is what those games ran with.
 */
export function seedRuleOverrides(seed: RecordedSeed): RuleOverrides {
  return {
    pointGoal: seed.pointGoal,
    mugginsStrict: seed.mugginsStrict,
    opening13: seed.opening13 ?? false,
    baldFinish: seed.baldFinish ?? false,
  };
}

/**
 * Per-step descriptor surfaced by `stepForward` so the host (ReplayScene)
 * can react to the engine transition without re-deriving it. Domino has no
 * per-tile animation today, so this is mostly informational.
 */
export interface ReplayStepEvent {
  readonly index: number;
  readonly move: RecordedMove;
  readonly preState: GameStateData;
  readonly postState: GameStateData;
  /** The visual fold the player picked at record time — chain layout reads it. */
  readonly turn: Turn | null;
}

// Synthetic clock for replay. `applyMove(state, seat, tile, arm, REPLAY_NOW)`
// sets `pendingMuggins.expiresAt = REPLAY_NOW + ruleSet.mugginsClaimWindowMs`.
// Subsequent `claimMuggins` calls pass `REPLAY_NOW` (still within the window),
// and `expireMuggins` passes `REPLAY_EXPIRY` (just after) so the expiry path
// triggers. Both lower-bound below `Date.now()` so they never collide with a
// real timer.
const REPLAY_NOW = 0;
const REPLAY_EXPIRY = 10 ** 12;

/**
 * Read-only replay engine over a recorded domino match. Drives a recomputed
 * `GameStateData` forward by re-applying each `RecordedMove`. Seek rebuilds
 * from the initial state (cheap — applyMove is pure and matches are < ~200
 * recorded events).
 */
export class ReplayController extends BaseReplayController {
  private readonly record: GameRecord;
  private readonly ruleSet: DominoRuleSet;
  private state: GameStateData;
  /**
   * Parallel side-state mirroring the recorded turns chosen at play time.
   * `turns[armIdx][tileIdx]` is the fold for the tile at that position. Lead
   * tiles populate every freshly-created arm with one `'straight'` entry.
   * Rebuilt on every seek alongside `state`.
   */
  private turns: Turn[][] = [];

  constructor(record: GameRecord) {
    super();
    this.record = record;
    this.ruleSet = this.buildRuleSet(record);
    this.state = createInitialState(this.ruleSet, record.playerCount, record.seed.seed);
  }

  getRecord(): GameRecord {
    return this.record;
  }

  getState(): GameStateData {
    return this.state;
  }

  /** Snapshot of the recorded turns at the current step — feeds `computeChainLayout`. */
  getTurns(): readonly (readonly Turn[])[] {
    return this.turns;
  }

  getTotalSteps(): number {
    return this.record.moves.length;
  }

  /** Apply the next recorded event and return a descriptor. Null when at end. */
  stepForward(): ReplayStepEvent | null {
    if (this.isAtEnd()) return null;
    const move = this.record.moves[this.stepIndex];
    if (!move) return null;
    const preState = this.state;
    const turn = this.applyMove(move);
    const event: ReplayStepEvent = {
      index: this.stepIndex,
      move,
      preState,
      postState: this.state,
      turn,
    };
    this.advanceIndex();
    return event;
  }

  protected reset(): void {
    this.state = createInitialState(this.ruleSet, this.record.playerCount, this.record.seed.seed);
    this.turns = [];
  }

  protected applyStep(index: number): void {
    const move = this.record.moves[index];
    if (!move) return;
    this.applyMove(move);
  }

  /**
   * Apply one recorded event to `this.state`, mirroring main.ts' reducer
   * dispatch. Returns the play's visual fold (or null for non-play events)
   * so callers can correlate with the rendered chain.
   */
  private applyMove(move: RecordedMove): Turn | null {
    switch (move.kind) {
      case 'play': {
        const result = applyMove(this.state, move.seat, move.tile, move.armIndex, REPLAY_NOW);
        this.state = result.state;
        this.recordTurn(move.armIndex, move.turn);
        return move.turn;
      }
      case 'draw':
        this.state = applyDraw(this.state, move.seat);
        return null;
      case 'pass':
        this.state = applyPass(this.state, move.seat);
        return null;
      case 'claim-muggins':
        this.state = claimMuggins(this.state, move.seat, REPLAY_NOW);
        return null;
      case 'expire-muggins':
        this.state = expireMuggins(this.state, REPLAY_EXPIRY);
        return null;
      case 'next-hand':
        this.state = startNextHand(this.state, move.seed);
        this.turns = [];
        return null;
    }
  }

  /** Mirror of main.ts' recordTurn — keeps `turns` aligned with the chain shape. */
  private recordTurn(armIndex: number, turn: Turn): void {
    while (this.turns.length < this.state.chain.arms.length) this.turns.push([]);
    if (armIndex === -1) {
      for (let i = 0; i < this.state.chain.arms.length; i += 1) {
        const tileCount = this.state.chain.arms[i]?.tiles.length ?? 0;
        const arr = this.turns[i]!;
        while (arr.length < tileCount) arr.push('straight');
      }
      return;
    }
    for (let i = 0; i < this.state.chain.arms.length; i += 1) {
      const arr = this.turns[i]!;
      const tileCount = this.state.chain.arms[i]?.tiles.length ?? 0;
      if (i === armIndex) {
        while (arr.length < tileCount - 1) arr.push('straight');
        arr.push(turn);
      } else {
        while (arr.length < tileCount) arr.push('straight');
      }
    }
  }

  /**
   * Materialize the rule set from the record. The seed's rule values were
   * chosen at game start, so they take precedence over the variant defaults;
   * `applyRuleOverrides` drops the ones that don't apply to the variant.
   */
  private buildRuleSet(record: GameRecord): DominoRuleSet {
    return applyRuleOverrides(getRuleSet(record.variant), seedRuleOverrides(record.seed));
  }
}
